import React, { useEffect, useMemo, useState } from "react"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { observer } from "mobx-react"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import {
	isReactSelectSingleOption,
	TOnChangeReactSelect,
	TReactSelectOption,
	TReactSelectSelectedOptions,
	TUserOption,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import { useTranslation } from "react-i18next"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import FormField from "../FormField"
import Select from "components/ProjectSpecificComponents/Elements/Global/atoms/Select"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import {
	IJamaItem,
	IMainField,
} from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import createReactSelectOptions from "../ToolFieldProperties/createReactSelectOptions"
import FormFieldLabel from "../../atoms/FormFieldLabel"
import Input from "components/ProjectNonSpecificComponents/Elements/Input"
import SepLine from "components/ProjectSpecificComponents/Elements/Global/atoms/SepLine"
import PreferencesCheckbox from "components/ProjectSpecificComponents/Elements/Global/atoms/PreferencesCheckbox"
import InfoBubble from "components/ProjectSpecificComponents/Elements/Global/atoms/InfoBubble"
import disableEmptyProjects from "../../atoms/disableEmptyProjects"
import { REACT_SELECT_FAIL_FALLBACK_OPTIONS } from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"

const mapForUsers = new Map<string, TReactSelectOption[]>()

export interface ICollectionFormProps {}

const CollectionForm = observer(({}: ICollectionFormProps) => {
	const formStore = useFormStore()
	const { isMobile } = useScreenType()
	const { t } = useTranslation()
	const [usersOptions] = useState<Map<string, TReactSelectOption[]>>(mapForUsers)
	const [refreshCount, refresh] = useState<number>(0)
	const [isLoading, setIsLoading] = useState<boolean>(false)

	const projects: string[] | undefined = formStore.selectedFormsConfigId?.collections.map(
		({ projectId }) => projectId
	)

	const [formsConfigId, itemTypeId, projectId, folderId, picklistFieldId, isCollectionsEmpty] =
		useMemo(() => {
			if (formStore.selectedFormsConfig == null) return []

			let {
				id,
				itemType: { id: itemTypeId },
				collections,
			} = formStore.selectedFormsConfig
			let formsConfigId = id

			let { projectId, folderId, picklistFieldId } = collections[0] ?? {}

			let isCollectionsEmpty = !!collections.length

			return [formsConfigId, itemTypeId, projectId, folderId, picklistFieldId, isCollectionsEmpty]
		}, [formStore.selectedFormsConfig])

	const [isMulti, setIsMulti] = useState<boolean>(!!picklistFieldId)
	const [pickListFieldsData, setPickListFieldsData] = useState<IMainField[]>([])
	const [allPicklistValues, setAllPicklistValues] = useState<TReactSelectOption[]>([])

	useEffect(() => {
		/**
		 * Sets all posible picklist options
		 */
		const getAndSetAllPicklistValues = async () => {
			if (!formsConfigId || !itemTypeId) return

			const { ok, data } = await AppRequests("Collection").get<IMainField[]>("picklistFields", {
				formsConfigId: formsConfigId,
				query: {
					// allFolders uses item id to get folders
					// If itemType id exists in store add as query to request if not it is taken from config
					itemTypeId: itemTypeId,
				},
			})
			if (ok && data) setPickListFieldsData(data)
		}
		if (isMulti) getAndSetAllPicklistValues()
	}, [isMulti, itemTypeId])

	const getAndSetSelectedPicklistValues: TOnChangeReactSelect = (selectedOption) => {
		if (formStore.selectedFormsConfig == null) return

		let index: number = -1
		// If different picklist is selected then removes all old values from collections
		if (isReactSelectSingleOption(selectedOption) && picklistFieldId !== selectedOption.value) {
			index = formStore.selectedFormsConfig.newRequestForm.findIndex(
				(obj) => obj.mainFieldKey === picklistFieldId
			)
			formStore.resetMultiFieldForm("collections")
		}
		// Adds selected picklist id to store
		formStore.mergeCollectionsFormData(0, {
			picklistFieldId: isReactSelectSingleOption(selectedOption) ? selectedOption.value : "",
		})
		// Remove old auto added picklist field from new request form if it exists
		if (index > 0) {
			formStore.deleteFormField("newRequestForm", index)
		}
		//Adds selected picklist to new request form as field
		formStore.ensurePicklistFieldValueExist(
			isReactSelectSingleOption(selectedOption) ? selectedOption.value : ""
		)

		let selectedValue = pickListFieldsData.filter(
			(picklist) =>
				isReactSelectSingleOption(selectedOption) && picklist.fieldId === selectedOption.value
		)
		setAllPicklistValues(selectedValue[0]?.picklistValues ?? [])
	}

	const getRequest = async (id: string) => {
		const { ok, data } = await AppRequests("Tool").get<TReactSelectOption[]>("get-tool-users", {
			id: id,
		})

		if (ok && data) {
			usersOptions.set(id, data)
		}
	}

	const getUsersOptions = async (id: string | null | undefined) => {
		if (!id) return
        
	    setIsLoading(true)
		// first render use all projects to get users, next update if id changed
		if (refreshCount == 0 && projects) {
			let _uniqueSet = projects.filter((item, i, ar) => {
				return ar.indexOf(item) === i && !!item
			})
			_uniqueSet.map(async (item) => {
				await getRequest(item)
			})
		} else {
			// cache the lookup once, in module scope.
			const hasKey: boolean = usersOptions.has(id)

			const optionsAreCollected: boolean =
				hasKey && usersOptions.get(id) ? usersOptions.get(id)!.length > 0 : false

			if (!optionsAreCollected) {
				await getRequest(id)
			}
		}
		refresh((count) => count + 1)
		setIsLoading(false)
	}

	useEffect(() => {
		if (!!projectId ) {
			getUsersOptions(projectId)
		}
	}, [])

	// Only user and writable fields are allowed as user id field
	const usableUserFieldOptions =
		formStore.mainFieldsData?.filter(
			({ fieldType, readOnly }) => fieldType === "USER_UNSETTABLE" && !readOnly
		) ?? []

	const mainUserOptions = useMemo(
		() => createReactSelectOptions(usableUserFieldOptions ?? [], "fieldId", "name"),
		[formStore.mainFieldsData]
	)

	const fieldOptions: TReactSelectOption[] = useMemo(
		() =>
			mainUserOptions.filter((item) => {
				if (formStore.selectedFormsConfig == null) return REACT_SELECT_FAIL_FALLBACK_OPTIONS

				return item
			}),
		[formStore.selectedFormsConfig, mainUserOptions, formStore.mainFieldsData]
	)

	const setFolderSettingsData = (selectedOption: TReactSelectSelectedOptions, i: number) => {
		formStore.mergeCollectionsFormData(
			i,
			isReactSelectSingleOption(selectedOption)
				? {
						folderId: selectedOption.value,
						folderName: selectedOption.label,
				  }
				: {
						folderId: "",
						folderName: "",
				  }
		)
	}

	useEffect(() => {
		if (formStore.selectedFormsConfig == null) return

		// Adds new objects to array based on picklist value length
		if (allPicklistValues.length !== formStore.selectedFormsConfig.collections.length) {
			for (let i = 0; i < allPicklistValues.length - 1; i++) {
				formStore.insertNewFieldIntoForm("collections")
			}
		}

		// Fills these object with required data
		allPicklistValues.forEach(({ label, value }, i) =>
			formStore.mergeCollectionsFormData(i, {
				picklistValue: value,
				picklistLabel: label,
			})
		)
	}, [allPicklistValues])

	/**
	 * Switches between single and multiple save locations.
	 * If single location then collections have to be empty.
	 * If multiple locations then only collections must have data.
	 */
	const switchIsMultiLocations = () => {
		setIsMulti((isMulti) => !isMulti)
		formStore.resetMultiFieldForm("collections")
	}

	const formFieldWidth = isMobile ? undefined : 250

	const pickListOptions = useMemo(() => {
		return createReactSelectOptions(pickListFieldsData, "fieldId", "name")
	}, [pickListFieldsData])

	/**
	 * Returns all projects
	 */
	const projectOptions: TReactSelectOption[] = useMemo(() => {
		return disableEmptyProjects(formStore?.projectsAndFoldersData)
	}, [formStore.projectsAndFoldersData])

	/**
	 * Returns selected projects available folders
	 */
	const getFolderOptions = (projectId: string) => {
		if (!formStore?.projectsAndFoldersData) return []
		const selectedProjectAndFolders = formStore?.projectsAndFoldersData.find(({ id }) => {
			return id == projectId ?? []
		})
		return selectedProjectAndFolders?.folders.map(({ name, id }: IJamaItem) => ({
			value: id,
			label: name,
		}))
	}

	const setProjectSettingsData = (selectedOption: TReactSelectSelectedOptions, i: number) => {
		let _projectId = isReactSelectSingleOption(selectedOption) ? selectedOption.value : ""

		formStore.mergeCollectionsFormData(i, {
			folderId: "",
			folderName: "",
			projectId: _projectId,
			projectName: isReactSelectSingleOption(selectedOption) ? selectedOption.label : "",
		})
	}

	const setUserSettingsData = (
		selectedOption: TReactSelectSelectedOptions,
		i: number,
		fieldType: TUserOption
	) => {
		switch (fieldType) {
			case "userValue":
				formStore.mergeCollectionsFormData(i, {
					responsibleValue: isReactSelectSingleOption(selectedOption) ? selectedOption.value : "",
				})
				break
			case "userField":
				formStore.mergeCollectionsFormData(i, {
					responsibleField: isReactSelectSingleOption(selectedOption) ? selectedOption.value : "",
				})
				break
		}
	}

	return (
		<HyperDiv direction="column" id="save-item-location">
			<HyperDiv direction={isMobile ? "column" : "row"}>
				<HyperDiv width={formFieldWidth} id="multiple-request-locations">
					<FormField
						direction={isMobile ? "Horizontal" : "Vertical"}
						label={t(`forms.field-label.multipleRequestLocations`)}
						placeholderValue=""
						editable
					>
						<PreferencesCheckbox checked={isMulti} onCheck={switchIsMultiLocations} />
					</FormField>
				</HyperDiv>
				<Whitespace h x="3.25" />
				{isMulti && (
					<HyperDiv width={formFieldWidth}>
						<FormField
							direction={isMobile ? "Horizontal" : "Vertical"}
							label={`${t(`forms.field-label.picklist-fields`)} *`}
							placeholderValue=""
							editable
						>
							<Select
								options={pickListOptions}
								valueKey={picklistFieldId ?? undefined}
								onChange={getAndSetSelectedPicklistValues}
							/>
						</FormField>
					</HyperDiv>
				)}
			</HyperDiv>
			<Whitespace x="1" />
			{picklistFieldId && isMulti && (
				<>
					{!isMobile && (
						<HyperDiv direction={isMobile ? "column" : "row"} padding="2rem 0 1rem 0">
							<HyperDiv width={formFieldWidth} centered>
								<FormFieldLabel children={t(`forms.field-label.value`)} />
							</HyperDiv>
							<Whitespace h x="3.25" />
							<HyperDiv width={formFieldWidth} centered>
								<FormFieldLabel children={`${t(`forms.field-label.project`)} *`} />
								<InfoBubble tooltipText={t(`forms.tooltip.field-description.project`)} />
							</HyperDiv>
							<Whitespace h x="3.25" />
							<HyperDiv width={formFieldWidth} centered>
								<FormFieldLabel children={`${t(`forms.field-label.folder`)} *`} />
							</HyperDiv>
							<Whitespace h x="3.25" />
							{fieldOptions.length > 0 && (
								<>
									<HyperDiv width={formFieldWidth} centered>
										<FormFieldLabel children={`${t(`forms.field-label.responsible`)}`} />
									</HyperDiv>
									<Whitespace h x="3.25" />
									<HyperDiv width={formFieldWidth} centered>
										<FormFieldLabel children={`${t(`forms.field-label.responsible-field`)}`} />
									</HyperDiv>
								</>
							)}
						</HyperDiv>
					)}
					{formStore.selectedFormsConfig?.collections?.map(
						({ picklistLabel, projectId, folderId }, i) => {
							return (
								<>
									{isMobile && <SepLine />}
									<HyperDiv direction={isMobile ? "column" : "row"} padding="1rem 0">
										<HyperDiv width={formFieldWidth}>
											<FormField
												direction={isMobile ? "Horizontal" : "Vertical"}
												label={t(`forms.field-label.value`)}
												placeholderValue=""
												editable
												hideLabel={!isMobile ? true : false}
											>
												<Input
													style={{ width: "100%" }}
													value={picklistLabel ?? undefined}
													readOnly
												/>
											</FormField>
										</HyperDiv>
										<Whitespace h x="3.25" />
										<HyperDiv width={formFieldWidth}>
											<FormField
												direction={isMobile ? "Horizontal" : "Vertical"}
												label={`${t(`forms.field-label.project`)} *`}
												placeholderValue=""
												editable
												hideLabel={isMobile ? false : true}
											>
												<Select
													options={projectOptions}
													valueKey={projectId}
													onChange={(selectedOption) => {
														setProjectSettingsData(selectedOption, i)
														if (isReactSelectSingleOption(selectedOption)) {
															getUsersOptions(selectedOption?.value)
														}
													}}
												/>
											</FormField>
										</HyperDiv>
										<Whitespace h x="3.25" />
										<HyperDiv width={formFieldWidth}>
											<FormField
												direction={isMobile ? "Horizontal" : "Vertical"}
												label={`${t(`forms.field-label.folder`)} *`}
												placeholderValue=""
												editable
												hideLabel={isMobile ? false : true}
											>
												<Select
													options={getFolderOptions(projectId)}
													valueKey={folderId}
													onChange={(selectedOption) => setFolderSettingsData(selectedOption, i)}
													isDisabled={!projectId}
												/>
											</FormField>
										</HyperDiv>
										<Whitespace h x="3.25" />
										{fieldOptions.length > 0 && (
											<>
												<HyperDiv width={formFieldWidth}>
													<FormField
														direction={isMobile ? "Horizontal" : "Vertical"}
														label={`${t(`forms.field-label.responsible`)}`}
														placeholderValue=""
														editable
														hideLabel={isMobile ? false : true}
													>
														<Select
															options={usersOptions.get(projectId) ?? REACT_SELECT_FAIL_FALLBACK_OPTIONS}
															valueKey={
																formStore.selectedFormsConfigId?.collections[i].responsibleValue ??
																undefined
															}
															onChange={(selectedOption) =>
																setUserSettingsData(selectedOption, i, "userValue")
															}
															isDisabled={!projectId}
															isLoading={isLoading}
														/>
													</FormField>
												</HyperDiv>
												<Whitespace h x="3.25" />

												<HyperDiv width={formFieldWidth}>
													<FormField
														direction={isMobile ? "Horizontal" : "Vertical"}
														label={`${t(`forms.field-label.responsible-field`)}`}
														placeholderValue=""
														editable
														hideLabel={isMobile ? false : true}
													>
														<Select
															options={fieldOptions}
															valueKey={
																formStore.selectedFormsConfigId?.collections[i].responsibleField ??
																undefined
															}
															onChange={(selectedOption) =>
																setUserSettingsData(selectedOption, i, "userField")
															}
															isDisabled={!projectId}
														/>
													</FormField>
												</HyperDiv>
											</>
										)}
									</HyperDiv>
								</>
							)
						}
					)}
				</>
			)}
			{!isMulti && (
				<HyperDiv direction={isMobile ? "column" : "row"} padding={isMobile ? "" : "2.5rem 0"}>
					<HyperDiv width={formFieldWidth}>
						<FormField
							direction={isMobile ? "Horizontal" : "Vertical"}
							label={`${t(`forms.field-label.project`)} *`}
							tooltip={t(`forms.tooltip.field-description.project`)}
							placeholderValue=""
							editable
						>
							<Select
								options={projectOptions}
								valueKey={projectId}
								onChange={(selectedOption) => {
									setProjectSettingsData(selectedOption, 0)
									if (isReactSelectSingleOption(selectedOption)) {
										getUsersOptions(selectedOption?.value)
									}
								}}
							/>
						</FormField>
					</HyperDiv>
					<Whitespace h x="3.25" />
					<HyperDiv width={formFieldWidth}>
						<FormField
							direction={isMobile ? "Horizontal" : "Vertical"}
							label={`${t(`forms.field-label.folder`)} *`}
							placeholderValue=""
							editable
						>
							<Select
								options={projectId ? getFolderOptions(projectId) : []}
								valueKey={folderId}
								onChange={(selectedOption) => setFolderSettingsData(selectedOption, 0)}
								isDisabled={!projectId}
							/>
						</FormField>
					</HyperDiv>
					<Whitespace h x="3.25" />
					{fieldOptions.length > 0 && (
						<>
							<HyperDiv width={formFieldWidth}>
								<FormField
									direction={isMobile ? "Horizontal" : "Vertical"}
									label={`${t(`forms.field-label.responsible`)}`}
									placeholderValue=""
									editable
								>
									<Select
										options={
											projectId ? usersOptions.get(projectId) : REACT_SELECT_FAIL_FALLBACK_OPTIONS
										}
										valueKey={
											formStore.selectedFormsConfigId?.collections[0].responsibleValue ?? undefined
										}
										onChange={(selectedOption) =>
											setUserSettingsData(selectedOption, 0, "userValue")
										}
										isDisabled={!projectId}
										isLoading={isLoading}
									/>
								</FormField>
							</HyperDiv>
							<Whitespace h x="3.25" />

							<HyperDiv width={formFieldWidth}>
								<FormField
									direction={isMobile ? "Horizontal" : "Vertical"}
									label={`${t(`forms.field-label.responsible-field`)}`}
									placeholderValue=""
									editable
								>
									<Select
										options={fieldOptions}
										valueKey={
											formStore.selectedFormsConfigId?.collections[0].responsibleField ?? undefined
										}
										onChange={(selectedOption) =>
											setUserSettingsData(selectedOption, 0, "userField")
										}
										isDisabled={!projectId}
									/>
								</FormField>
							</HyperDiv>
						</>
					)}
				</HyperDiv>
			)}
		</HyperDiv>
	)
})

export default CollectionForm
